import { getBreakPointReturnType, getRelatedBreakpoint, useViewportWidth } from '@/Utility/hooks';
import { PAGE_LAYOUT } from '@/inertia/Shared/Page';
import { atom, useAtom } from 'jotai';
import { debounce } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import type { ImperativePanelGroupHandle, ImperativePanelHandle } from 'react-resizable-panels';
import type { PanelCallbacks } from './PanelWrapper';

/**
 * The type of the panel listeners
 */
type PanelListeners<T extends keyof PanelCallbacks = keyof PanelCallbacks> = Map<
  PanelName, // PanelName - left, right
  Map<T, Array<PanelCallbacks[T]>>
>;

const panelProvider = Object.freeze({
  panelGroup: { current: null } as React.RefObject<ImperativePanelGroupHandle | null>,

  leftPanel: { current: null } as React.RefObject<ImperativePanelHandle | null>,
  leftPanelSize: { current: null } as React.RefObject<number | null>,

  rightPanel: { current: null } as React.RefObject<ImperativePanelHandle | null>,

  listeners: { current: new Map() } as React.RefObject<PanelListeners>,
});

export type PanelProviderType = typeof panelProvider;

type PanelName = 'left' | 'right';

export const PanelProvider = React.createContext<typeof panelProvider>(panelProvider);

/**
 * Get the panel context
 */
export const usePanelContext = () => {
  return React.useContext<typeof panelProvider>(PanelProvider);
};

const useHasPanelContext = () => {
  const panelContext = usePanelContext();

  return panelContext.panelGroup.current !== null;
};

/**
 * use panel callback - onResize, onCollapse, onExpand
 */
export const usePanelCallback = <T extends keyof PanelCallbacks>(
  name: PanelName,
  key: T,
  callback: PanelCallbacks[T]
) => {
  const listeners = usePanelContext().listeners;

  useEffect(() => {
    if (!listeners.current.has(name)) {
      listeners.current.set(name, new Map());
    }

    if (!listeners.current?.get(name)?.has(key)) {
      listeners.current.get(name)?.set(key, []);
    }

    listeners.current.get(name)?.get(key)?.push(callback);

    return () => {
      const panelCallback = listeners.current.get(name)?.get(key);
      const callbackIdx = panelCallback?.indexOf?.(callback);

      if (callbackIdx && callbackIdx > -1) {
        listeners.current.get(name)?.get(key)?.splice(callbackIdx, 1);
      }
    };
  }, [listeners]);
};

const RightPanelSizeAtom = atom(0);
const LeftPanelSizeAtom = atom(0);

export const usePanelSizeAtom = (panel: 'right' | 'left') => {
  const [size, setSize] = useAtom(panel === 'right' ? RightPanelSizeAtom : LeftPanelSizeAtom);

  const setSizeDebounced = debounce(setSize, 250, { leading: false, trailing: true });

  return [size, setSizeDebounced] as const;
};

const computePanelGridSize = (currentPanelSize: number) => {
  if (currentPanelSize <= 250) {
    return 12;
  }
  if (currentPanelSize <= 500) {
    return 6;
  }
  if (currentPanelSize <= 750) {
    return 4;
  }
  if (currentPanelSize <= 1000) {
    return 3;
  }

  return 2;
};

/**
 * Get the right panel grid size
 * This is calculated using the view port width and the right panel size
 */
export const usePanelGridSize = (panel: 'right' | 'left') => {
  const [panelSize] = useAtom(panel === 'right' ? RightPanelSizeAtom : LeftPanelSizeAtom); // 0-100
  const viewPortWidth = useViewportWidth();

  const panelGridSize = useMemo(() => {
    return computePanelGridSize(Math.floor((viewPortWidth / 100) * (panelSize === 0 ? 100 : panelSize)));
  }, [panelSize, viewPortWidth]);

  return panelGridSize;
};

/**
 * Get the panel breakpoint
 */
export const usePanelBreakpoint = (panel: 'right' | 'left') => {
  const [panelSize] = useAtom(panel === 'right' ? RightPanelSizeAtom : LeftPanelSizeAtom); // 0-100
  const viewPortWidth = useViewportWidth();

  const hasPanelContext = useHasPanelContext();

  const panelViewPortWidth = useMemo(() => {
    if (panelSize === 0 || !hasPanelContext) {
      return viewPortWidth;
    }
    return Math.floor((viewPortWidth / 100) * panelSize);
  }, [panelSize, viewPortWidth, hasPanelContext]);

  const currentBreakpoint = useMemo(() => {
    return getRelatedBreakpoint({
      viewportWidth: panelViewPortWidth,
    });
  }, [panelViewPortWidth]);

  return getBreakPointReturnType(currentBreakpoint);
};

export const usePanelPageLayout = () => {
  const { isMobile } = usePanelBreakpoint('left');

  const layout = useMemo(() => {
    if (isMobile) {
      return {
        ...PAGE_LAYOUT,
        leftProps: {
          xs: 12,
          md: 12,
        },
        rightProps: {
          xs: 12,
          md: 12,
        },
      };
    }

    return {
      ...PAGE_LAYOUT,
      leftProps: {
        xs: 8,
      },
      rightProps: {
        xs: 4,
      },
    };
  }, [isMobile]);

  return layout;
};

export type { PanelListeners, PanelName };
